import { Plus, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getChargeImages } from '../api/chargeImages';
import type { ChargeOverviewLineItemDto } from '../types/jobCostOverview';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

interface ContractsPanelProps {
  jobId: number;
  charges: ChargeOverviewLineItemDto[];
  chargeTotal: number;
  onAddCharge: () => void;
  onEditCharge: (charge: ChargeOverviewLineItemDto) => void;
}

export function ContractsPanel({ jobId, charges, chargeTotal, onAddCharge, onEditCharge }: ContractsPanelProps) {
  const handleViewImage = async (e: React.MouseEvent, chargeId: number) => {
    e.stopPropagation();
    const images = await getChargeImages(jobId, chargeId);
    if (images.length > 0) {
      window.open(images[0].filePath, '_blank');
    }
  };
  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-gray-50 rounded-t-lg">
        <h4 className="text-xs font-semibold">Contracts / Charges</h4>
        <Button variant="ghost" size="sm" onClick={onAddCharge} className="h-6 text-xs px-2">
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>
      <div className="p-2">
        {charges.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">No charges</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="w-4 pb-1"></th>
                <th className="text-left pb-1 font-medium">Date</th>
                <th className="text-left pb-1 font-medium">Description</th>
                <th className="text-right pb-1 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => (
                <tr
                  key={charge.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onEditCharge(charge)}
                >
                  <td className="py-0.5">
                    {charge.hasImages && (
                      <button
                        onClick={(e) => handleViewImage(e, charge.id)}
                        className="hover:text-secondary transition-colors"
                        title="View document (opens in new tab)"
                      >
                        <ImageIcon className="h-4 w-4 text-secondary" />
                      </button>
                    )}
                  </td>
                  <td className="py-0.5">{formatDate(charge.chargeDate)}</td>
                  <td className="py-0.5 truncate max-w-[120px]">{charge.description || '—'}</td>
                  <td className={`py-0.5 text-right ${charge.amount < 0 ? 'text-red-600' : ''}`}>
                    {formatCurrency(charge.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex justify-between items-center pt-1.5 mt-1.5 border-t border-border text-xs font-semibold">
          <span>Total</span>
          <span>{formatCurrency(chargeTotal)}</span>
        </div>
      </div>
    </div>
  );
}
